import React, { useEffect, useMemo, useCallback, useState } from 'react';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import { EventBusy, EventRepeat, Lock } from '@mui/icons-material';
import { Box } from '@mui/material';
import { exibeValorLocalizado, exibeValorResumido } from '../../../../shared';
import CorrenteFechada from '../../../../assets/img/corrente_fechada.svg';
import { nivelUsuarioEnum } from '../../../../shared/Enums';
import { getListarInfosProdutoPipeComercial } from '../../../../api/dashboard';

const columnConfigs = dadosUser => {
  let colunas = [
    {
      key: 'limiteCredito',
      nomeColuna: 'Limite Crédito',
      accessor: produto => produto.limite_credito,
      getId: produto => `limite_${produto.codProduto}`
    },
    {
      key: 'limiteExtra',
      nomeColuna: 'Limite Extra',
      accessor: produto => produto.limite_extra,
      getId: produto => `risco_${produto.codProduto}`
    },
    {
      key: 'limiteComercial',
      nomeColuna: 'Limite Comercial',
      accessor: produto => produto.valorLimiteComercial,
      getId: produto => `limiteComercial_${produto.codProduto}`
    },
    {
      key: 'risco',
      nomeColuna: 'Risco',
      accessor: produto => produto.risco,
      getId: produto => `risco_${produto.codProduto}`
    },
    {
      key: 'saldoCredito',
      nomeColuna: 'Saldo Crédito',
      accessor: produto => produto.saldo_credito,
      getId: produto => `saldoCredito_${produto.codProduto}`
    },
    {
      key: 'saldoComercial',
      nomeColuna: 'Saldo Comercial',
      accessor: produto => produto.saldoComercial,
      getId: produto => `saldoComercial_${produto.codProduto}`
    },
    {
      key: 'tranche',
      nomeColuna: 'Tranche',
      accessor: produto => produto.tranche,
      getId: produto => `tranche_${produto.codProduto}`
    },
    {
      key: 'saldoTranche',
      nomeColuna: 'Saldo Tranche',
      accessor: produto => produto.saldoTranche,
      getId: produto => `saldoTranche_${produto.codProduto}`
    }
  ];

  if (dadosUser && dadosUser.nivel === nivelUsuarioEnum.CLIENTE) {
    const camposAExcluir = ['limiteComercial', 'saldoComercial'];
    colunas = colunas.filter(e => !camposAExcluir.includes(e.key));
  }

  return colunas;
};

const useModalGridDashboardExibirProdutosHook = ({
  ModalGridExibirProdutos,
  setModalGridExibirProdutos,
  dadosUser,
  setAlerta
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [dadosAdicionaisProduto, setDadosAdicionaisProduto] = useState({});

  useEffect(() => {
    if (ModalGridExibirProdutos.params.id) {
      setIsLoading(true);
      getListarInfosProdutoPipeComercial(ModalGridExibirProdutos.params.id)
        .then(res => {
          if (!res.data.success) {
            setAlerta({
              mensagem:
                res.data.mensagem ||
                'Ocorreu um erro ao buscar dados de produto. Favor contatar o suporte.',
              tipo: 'error'
            });
            setModalGridExibirProdutos(false);
            return;
          }
          setDadosAdicionaisProduto(res.data.data);
        })
        .catch(() => {
          setAlerta({
            mensagem: 'Ocorreu um erro ao buscar dados de produto. Favor contatar o suporte.',
            tipo: 'error'
          });
          setModalGridExibirProdutos(false);
        })
        .finally(() => setIsLoading(false));
    }
  }, [ModalGridExibirProdutos, setAlerta, setModalGridExibirProdutos]);

  const params = ModalGridExibirProdutos.params || {};

  const {
    lstProdutosCliente = [],
    lstDadosProduto = {},
    lstProdutosConjunto = []
  } = dadosAdicionaisProduto;

  const productArray = useMemo(() => {
    if (!params.infoProdutos) return [];

    return Object.entries(params.infoProdutos)
      .filter(([key]) => lstProdutosCliente.includes(Number(key)))
      .map(([key, info]) => {
        const dadosAdicionais = lstDadosProduto[key] || {};
        const valorLimiteComercial = dadosAdicionais.valorLimiteComercial || 0;
        const tranche = dadosAdicionais.tranche || 0;
        const saldoTranche = dadosAdicionais.saldoTranche || 0;
        const risco = info.risco || 0;
        const saldoComercial = valorLimiteComercial - risco;

        return {
          id: Number(key),
          ...info,
          valorLimiteComercial,
          tranche,
          saldoTranche,
          saldoComercial
        };
      });
  }, [params.infoProdutos, lstProdutosCliente, lstDadosProduto]);

  const limitesVencidos = useMemo(() => {
    if (!params.infoProdutos) return [];
    return Object.entries(params.infoProdutos)
      .map(([key, info]) => {
        const status = info.statusLimite;
        if (status && status.tipoStatus && status.tipoStatus !== 'bloqueado') {
          return {
            id: key,
            nomeProduto: `${info.nomeProduto} (${info.siglaProduto})`,
            msg:
              status.tipoStatus === 'vencido'
                ? ` venceu em ${status.vencimentoLimite}.`
                : ` vence em ${status.vencimentoLimite}.`
          };
        }
      })
      .filter(Boolean);
  }, [params.infoProdutos]);

  const produtosConjunto = useMemo(() => {
    return lstProdutosConjunto
      .map(
        prod =>
          params.infoProdutos &&
          params.infoProdutos[prod.codProduto] &&
          params.infoProdutos[prod.codProduto].nomeProduto
      )
      .filter(Boolean);
  }, [lstProdutosConjunto, params.infoProdutos]);

  const formatarListaProdutos = useCallback(array => {
    if (!array || !array.length) return '';
    if (array.length === 1) return array[0];
    return `${array.slice(0, -1).join(', ')} e ${array[array.length - 1]}.`;
  }, []);

  const limiteRiscoDisponivel = useCallback(
    (valorSemFormatacao, produto, coluna) => {
      let codProduto = produto.id;
      let val = parseInt(valorSemFormatacao, 10) || 0;
      const negativa = val < 0;
      if (negativa) {
        val = Math.abs(val);
      }
      const statusLim = coluna !== 'risco' ? produto.statusLimite || false : false;
      if (val === 0) {
        return <HorizontalRuleIcon className="texto-cor-cinza-claro" />;
      }

      return (
        <Box className="texto-nowrap-overflow-dotted">
          {statusLim &&
            statusLim.tipoStatus === 'bloqueado' &&
            coluna === 'limiteCredito' &&
            dadosUser &&
            dadosUser.nivel !== nivelUsuarioEnum.CLIENTE && (
              <span title="Limite Bloqueado">
                <Lock className="texto-danger ajustar-icone-direita bloqueado" />
              </span>
            )}
          {statusLim && statusLim.tipoStatus === 'vencido' && coluna === 'limiteCredito' && (
            <span title={`Limite venceu em ${statusLim.vencimentoLimite}`}>
              <EventBusy className="texto-danger ajustar-icone-direita vencido" />
            </span>
          )}
          {statusLim && statusLim.tipoStatus === 'vai_vencer' && coluna === 'limiteCredito' && (
            <span title={`Limite vence em ${statusLim.vencimentoLimite}`}>
              <EventRepeat className="texto-warning ajustar-icone-direita aVencer" />
            </span>
          )}
          <span
            title={`R$ ${negativa ? '-' : ''}${exibeValorLocalizado(val)}`}
            className={
              negativa ||
              (coluna === 'limiteCredito' && (statusLim === 'bloqueado' || statusLim === 'vencido'))
                ? 'texto-danger'
                : ''
            }
          >
            {`${negativa ? ' -' : ' '}${exibeValorResumido(val)}`}
          </span>
          {codProduto &&
            lstProdutosConjunto.some(prod => prod.codProduto === Number(codProduto)) &&
            coluna === 'limiteCredito' && (
              <img
                className="margin-left-3 margin-neg-top-5"
                src={CorrenteFechada}
                title="Valor do conjunto"
                alt="Valor do conjunto"
                width={13}
                height={13}
              />
            )}
        </Box>
      );
    },
    [dadosUser, lstProdutosConjunto]
  );

  return {
    params,
    productArray,
    limitesVencidos,
    produtosConjunto,
    formatarListaProdutos,
    limiteRiscoDisponivel,
    columnConfigs,
    isLoading
  };
};

export default useModalGridDashboardExibirProdutosHook;
